// src/components/common/SentToDisplay.js
import React, { useState } from "react";
import {
	Box,
	Avatar,
	Button,
	Chip,
	Typography,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";

export function SentToDisplay({
	recipients,
	triggerLabel,
	dialogTitleOverride,
	summaryMode, // "sent" | "claimed"
	disableDialogWhenEmpty = true,
	onTriggerClick,
	dialogSecondaryActionLabel,
	onDialogSecondaryAction,
}) {
	const [open, setOpen] = useState(false);
	const normalizedRecipients = (Array.isArray(recipients) ? recipients : []).map((entry) => {
		if (typeof entry === "object" && entry !== null) {
			const name = String(entry?.name || "").trim() || "Connection";
			const subtitle = String(entry?.subtitle || "").trim();
			const avatarUrl = String(entry?.avatarUrl || "").trim();
			const statusLabel = String(entry?.statusLabel || "").trim();
			const statusColor = String(entry?.statusColor || "").trim() || "default";
			return { name, subtitle, avatarUrl, statusLabel, statusColor };
		}
		const fallbackName = String(entry || "").trim() || "Connection";
		return { name: fallbackName, subtitle: "", avatarUrl: "", statusLabel: "", statusColor: "default" };
	});
	const isClaimMode = summaryMode === "claimed";

	const count = normalizedRecipients.length;
	const triggerText = triggerLabel || (isClaimMode
		? `Buried for ${count} ${count === 1 ? "person" : "people"}`
		: `Sent to ${count} ${count === 1 ? "person" : "people"}`);
	const dialogTitle = dialogTitleOverride || (isClaimMode ? "Buried for" : "My Contacts");

	const canOpen = !disableDialogWhenEmpty || count > 0;
	const hasTriggerAction = typeof onTriggerClick === "function";
	const getInitials = (name) => {
		const label = String(name || "").trim();
		if (!label) return "C";
		const parts = label.split(/\s+/).filter(Boolean);
		if (!parts.length) return "C";
		if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
		return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
	};
	const handleOpen = () => {
		if (hasTriggerAction) {
			onTriggerClick();
			return;
		}
		if (!canOpen) return;
		setOpen(true);
	};
	const handleClose = () => setOpen(false);

	return (
		<>
			{/* Trigger button summarizing count */}
			<Box sx={{ textAlign: "left", mb: 0 }}>
				<Button
					size="small"
					variant="text"
					onClick={handleOpen}
					disabled={!hasTriggerAction && !canOpen}
					sx={(theme) => ({
						textTransform: "none",
						fontWeight: 600,
						color:
							theme.palette.mode === "dark"
								? theme.palette.text.secondary
								: theme.colors.blue,
						textDecoration: hasTriggerAction ? "underline" : "none",
					})}
				>
					{triggerText}
				</Button>
			</Box>

				{/* Full list in a dialog */}
				<Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
					<DialogTitle>{dialogTitle}</DialogTitle>
					<DialogContent dividers>
						{normalizedRecipients.map((entry, idx) => {
							const name = String(entry?.name || "").trim() || "Connection";
							const subtitle = String(entry?.subtitle || "").trim();
							const avatarUrl = String(entry?.avatarUrl || "").trim();
							const statusLabel = String(entry?.statusLabel || "").trim();
							const statusColor = String(entry?.statusColor || "").trim() || "default";
							return (
								<Box
									key={`${name}-${idx}`}
									sx={{
										display: "flex",
										width: "100%",
										py: 0.75,
										alignItems: "center",
										gap: 1,
										borderBottom: "1px solid",
										borderColor: "divider",
									}}
								>
									<Avatar src={avatarUrl || undefined} sx={{ width: 34, height: 34 }}>
										{avatarUrl ? null : getInitials(name)}
									</Avatar>
									<Box sx={{ minWidth: 0 }}>
										<Typography
											variant="body2"
											sx={{
                        textAlign: "left",
                        fontWeight: 700,
                        whiteSpace: "normal",
                        overflow: "visible",
                        textOverflow: "clip",
                        wordBreak: "break-word",
                      }}
										>
											{name}
										</Typography>
										{subtitle ? (
											<Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          opacity: 0.75,
                          whiteSpace: "normal",
                          overflow: "visible",
                          textOverflow: "clip",
                          wordBreak: "break-word",
                        }}
                      >
												{subtitle}
											</Typography>
										) : null}
									</Box>
									{statusLabel ? (
										<Chip
											label={statusLabel}
											size="small"
											color={statusColor === "success" ? "success" : "default"}
											variant="outlined"
											sx={{ ml: "auto", fontWeight: 600 }}
										/>
									) : null}
								</Box>
							);
						})}
					</DialogContent>
				<DialogActions
					sx={{
						px: 2,
						py: 1.5,
						gap: 1.25,
						justifyContent: "flex-end",
					}}
				>
					{typeof onDialogSecondaryAction === "function" ? (
						<Button
							variant="contained"
							onClick={onDialogSecondaryAction}
							startIcon={<ShareIcon fontSize="small" />}
							sx={(theme) => ({
								textTransform: "none",
								borderRadius: "999px",
								fontWeight: 700,
								px: 2.25,
								backgroundColor: theme.colors.blue,
								color: theme.colors.white,
								minWidth: 96,
								"&:hover": {
									backgroundColor: theme.colors.darkBlue || theme.colors.blue,
								},
							})}
						>
							{dialogSecondaryActionLabel || "Share link"}
						</Button>
					) : null}
					<Button
						variant="outlined"
						onClick={handleClose}
						sx={{
							textTransform: "none",
							borderRadius: "999px",
							fontWeight: 700,
							px: 2.25,
							minWidth: 96,
						}}
					>
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
